import { useMemo, useState } from 'react';

import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace';
import { BANDS_DJS_TAXONOMY_KEY } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { StorefrontCardView } from '@zola/svc-marketplace-ts-types';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { TagV2Variant } from '@zola/zola-ui/src/components/Tag/TagV2.styles';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';
import H from '@zola/zola-ui/src/typography/Headings';
import P from '@zola/zola-ui/src/typography/Paragraphs';

import cx from 'classnames';

import { CardShelf } from '~/components/common/cards/CardShelf/CardShelf';
import { storefrontCardViewToVendorCardVendor } from '~/components/common/cards/util/helper';
import { VendorCard, VendorCardProps } from '~/components/common/cards/VendorCard/VendorCard';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { TrackingProductLocation } from '~/util/trackingHelper';

import { VendorDrawer } from '../EditPreferences/VendorDrawer';
import { EmptyState } from './EmptyState';
import { LowSupplyCard } from './LowSupplyCard';
import { NUM_TOP_PICKS } from './topPicksConstants';
import { useDismissTopPicks } from './useDismissTopPicks';
import { useTrackTopPicks } from './useTrackTopPicks';

import styles from './topPicks.module.less';

type TopPickBaseProps = React.HTMLAttributes<HTMLElement> & {
  // eslint-disable-next-line react/no-unused-prop-types
  vendorCardProps?: Omit<Partial<VendorCardProps>, 'vendor' | 'position'>;
  fromSrp: boolean;
  isPricingRatingTest1: boolean;
  isPricingRatingTest2: boolean;
};

export type TopPicksContentProps = TopPickBaseProps & {
  topPicks: StorefrontCardView[];
  lowSupply?: boolean;
  taxonomyKey: VendorTaxonomyKey;
};

export const TopPicksContent = ({
  topPicks,
  vendorCardProps,
  lowSupply = false,
  taxonomyKey,
  className,
  fromSrp,
  isPricingRatingTest1,
  isPricingRatingTest2,
  ...rest
}: TopPicksContentProps) => {
  const { isDesktop } = useResponsiveDesign();

  const [showEditPreferences, setShowEditPreferences] = useState(false);

  const isUnderPricingRatingTest = isPricingRatingTest1 || isPricingRatingTest2;

  const cards = useMemo(() => {
    let newCards = topPicks.map((vendor, index) => (
      <VendorCard
        key={vendor.storefrontUuid}
        vendor={storefrontCardViewToVendorCardVendor(vendor)}
        position={index}
        location={TrackingProductLocation.TOP_PICKS_DRAWER}
        showBadgeList
        showDetails={false}
        // We'd really like this to be _off_ if its a touch device
        showCarousel={isDesktop}
        {...vendorCardProps}
        trackClick={false}
        fromSrp={fromSrp}
        isPricingRatingTest1={isPricingRatingTest1}
        isPricingRatingTest2={isPricingRatingTest2}
        showVendorType={
          isUnderPricingRatingTest ? vendor.taxonomyNodeKey === BANDS_DJS_TAXONOMY_KEY : true
        }
      />
    ));

    if (lowSupply) {
      newCards = [
        ...newCards,
        <LowSupplyCard
          key="low-supply-card"
          taxonomyKey={taxonomyKey}
          showEditPreferences={() => setShowEditPreferences(true)}
          className={styles.lowSupplyCard}
        />,
      ];
    }
    return newCards;
  }, [
    fromSrp,
    isDesktop,
    isPricingRatingTest1,
    isPricingRatingTest2,
    isUnderPricingRatingTest,
    lowSupply,
    taxonomyKey,
    topPicks,
    vendorCardProps,
  ]);

  if (topPicks.length === 0) {
    return (
      <div className={cx(styles.topPicks, className)} {...rest}>
        <EmptyState
          taxonomyKey={taxonomyKey}
          showEditPreferences={() => setShowEditPreferences(true)}
        />
        <VendorDrawer displayDrawer={showEditPreferences} setShowDrawer={setShowEditPreferences} />
      </div>
    );
  }

  return (
    <div className={cx(styles.topPicks, className)} {...rest}>
      <H.Title4 className={styles.hed}>
        <span className={styles.strong}>Personalized recs</span> to help get you started
      </H.Title4>
      <P.BodyBase>
        Scroll down to explore even more vendors.{' '}
        <LinkV2 onClick={() => setShowEditPreferences(true)} noTextTransform inline>
          Want new recs? Edit <span className="hidden-xs">your</span> preferences
        </LinkV2>
      </P.BodyBase>
      <CardShelf className={styles.topPicksCards}>{cards}</CardShelf>
      <VendorDrawer displayDrawer={showEditPreferences} setShowDrawer={setShowEditPreferences} />
    </div>
  );
};

export const TopPicks = ({
  className,
  fromSrp,
  isPricingRatingTest1,
  isPricingRatingTest2,
}: TopPickBaseProps) => {
  const { vendorSearch, topPicks } = useSearchResultsPageContext();

  const { selectedTaxonomy } = vendorSearch;

  useTrackTopPicks({ topPicks });
  useDismissTopPicks({ taxonomyKey: selectedTaxonomy?.key });

  return (
    <TopPicksContent
      topPicks={topPicks}
      className={className}
      vendorCardProps={{
        badgeColorVariant: TagV2Variant.BLACK_010,
        className: styles.topPickCard,
      }}
      taxonomyKey={selectedTaxonomy?.key as VendorTaxonomyKey}
      lowSupply={topPicks.length < NUM_TOP_PICKS}
      fromSrp={fromSrp}
      isPricingRatingTest1={isPricingRatingTest1}
      isPricingRatingTest2={isPricingRatingTest2}
    />
  );
};
